"use client";

// THE TASK CHIP: the top bar's link to the Operations Inbox, and the card the
// reader opened from it.
//
//   no card open   "Inbox 6/30": today's cards done of total, a link to /inbox
//   a card open    "← Review sample requests · Lumen Skincare 3/37 +5 new", ✓
//                  once done; the whole chip goes back to the inbox exactly as
//                  it was when the card was opened
//
// ⚠️ THE OPEN CARD IS THE BROWSER TAB'S, NOT THE URL'S. A card opens its page
// with `?card=<id>`, but the page's own links drop it (a conversation picked on
// Messages, a filter on the requests tab), and the card must stay in the top
// bar while the reader works. So the card and the inbox address it was opened
// from are kept in `sessionStorage` (one tab's, as the work is), and the chip
// carries the card for as long as the reader stays under its place (the
// brand's affiliate pages); leaving that ends it.
//
// ⚠️ THE COUNTER IS THE SERVER'S, ASKED AGAIN AFTER EACH PIECE OF WORK. GET
// /api/inbox/chip brings the card up to date with the tick's own reading
// (lib/server/ops-inbox-chip.ts). The work pages re-render after every decision
// (router.refresh), and a re-render of a page carrying <CardWork> publishes a
// new stamp here: that, or a new address, is what asks again.
//
// ⚠️ ONE DAY, ONE READING: the chip's "6/30", the sidebar's badge (24 left) and,
// on the inbox, its summary line. Three reads would be three moments, and
// another person's card done between them made them disagree (main went red on
// it, 2026-11-04). So the provider holds the day and the badge reads it here.
// The inbox page hands over its own reading (<InboxDay>) and the chip does not
// ask there; anywhere else the chip's answer is the day.

import clsx from "clsx";
import { ArrowLeft, Check, Inbox } from "lucide-react";
import { createContext, useCallback, useContext, useEffect, useMemo, useState, useSyncExternalStore } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import { IntentLink } from "@/components/intent-link";
import { usePersona } from "@/components/persona-context";
import { LENS_HEADER, lensHref } from "@/lib/lens-url";
import { INBOX_PATH, underPlace, type DayTally, type TaskChip as Chip } from "@/lib/ops-inbox-page";
import { canOpen } from "@/lib/route-access";

const STORE_KEY = "ops-inbox-open-card";
const STORE_EVENT = "ops-inbox-open-card";

/** The card this tab has open, the inbox address it came from, and the place it is carried under. */
interface OpenCard {
  card: string;
  back: string | null;
  place: string | null;
}

function rawOpen(): string | null {
  try {
    return window.sessionStorage.getItem(STORE_KEY);
  } catch {
    return null;
  }
}

function parseOpen(raw: string | null): OpenCard | null {
  try {
    const v = JSON.parse(raw ?? "null") as OpenCard | null;
    return v && typeof v.card === "string" ? v : null;
  } catch {
    return null;
  }
}

const readOpen = (): OpenCard | null => parseOpen(rawOpen());

function writeOpen(v: OpenCard | null): void {
  const next = v === null ? null : JSON.stringify(v);
  if (next === rawOpen()) return;
  try {
    if (next === null) window.sessionStorage.removeItem(STORE_KEY);
    else window.sessionStorage.setItem(STORE_KEY, next);
  } catch {
    // A browser that refuses storage keeps no card: the chip falls back to the inbox's count.
  }
  window.dispatchEvent(new Event(STORE_EVENT));
}

const subscribe = (notify: () => void) => {
  window.addEventListener(STORE_EVENT, notify);
  return () => window.removeEventListener(STORE_EVENT, notify);
};

/** The open card as this tab keeps it; none on the server, so the first paint is the inbox's chip. */
function useOpenCard(): OpenCard | null {
  const raw = useSyncExternalStore(subscribe, rawOpen, () => null);
  return useMemo(() => parseOpen(raw), [raw]);
}

/** Called as a card's link is followed from the inbox: the ← comes back to this address. */
export function rememberOpenCard(cardId: string): void {
  // Kept quietly: the chip here stays the inbox's until the card's page opens (a link opened in a new tab copies it there).
  try {
    window.sessionStorage.setItem(STORE_KEY, JSON.stringify({ card: cardId, back: `${window.location.pathname}${window.location.search}`, place: null } satisfies OpenCard));
  } catch {
    // No storage: the ← goes to the inbox's front instead.
  }
}

// ─── Work done on the page ──────────────────────────────────────────────────

/** Today's tally, and the lens it was counted for: a day counted for another lens is nobody's on screen. */
interface HeldDay {
  lensKey: string | null;
  day: DayTally;
}

interface Work {
  stamp: number;
  setStamp: (n: number) => void;
  held: HeldDay | null;
  setHeld: (d: HeldDay) => void;
}

const WorkCtx = createContext<Work>({ stamp: 0, setStamp: () => {}, held: null, setHeld: () => {} });

export function TaskChipProvider({ children }: { children: React.ReactNode }) {
  const [stamp, setStamp] = useState(0);
  const [held, setHeldState] = useState<HeldDay | null>(null);
  const setHeld = useCallback((d: HeldDay) => {
    setHeldState((was) => (was && was.lensKey === d.lensKey && was.day.done === d.day.done && was.day.total === d.day.total ? was : d));
  }, []);
  const value = useMemo(() => ({ stamp, setStamp, held, setHeld }), [stamp, held, setHeld]);
  return <WorkCtx.Provider value={value}>{children}</WorkCtx.Provider>;
}

/** Today's tally for the lens on screen, as the chip shows it; null until it is known. */
export function useInboxDay(): DayTally | null {
  const { lensKey } = usePersona();
  const { held } = useContext(WorkCtx);
  return held && held.lensKey === lensKey ? held.day : null;
}

/**
 * Renders nothing; the inbox page's own reading of the day, handed to the chip
 * and the badge, so the three numbers on the inbox are one reading. Drawn again
 * on each re-render (router.refresh after a move), so it follows the work.
 */
export function InboxDay({ done, total }: DayTally) {
  const { lensKey } = usePersona();
  const { setHeld } = useContext(WorkCtx);
  useEffect(() => setHeld({ lensKey, day: { done, total } }), [lensKey, done, total, setHeld]);
  return null;
}

/**
 * Renders nothing; tells the chip the page was drawn again (`at`, the render's
 * time), so the open card's counter is asked again. A page where a card's work
 * is done carries one: its re-render after each decision is the signal.
 */
export function CardWork({ at }: { at: number }) {
  const { setStamp } = useContext(WorkCtx);
  useEffect(() => setStamp(at), [at, setStamp]);
  return null;
}

// ─── The chip ───────────────────────────────────────────────────────────────

// Capped by the screen on a phone, so the cluster it sits in (which never shrinks) still fits beside the persona menu.
const CHIP =
  "min-w-0 max-w-[30vw] sm:max-w-[460px] items-center gap-2 rounded-lg border px-3 py-1.5 text-[12px] font-semibold whitespace-nowrap transition-colors";

export function TaskChip() {
  const { persona, lensKey } = usePersona();
  const show = canOpen(persona.role, INBOX_PATH);
  return show ? <ChipFor lensKey={lensKey} /> : null;
}

function ChipFor({ lensKey }: { lensKey: string | null }) {
  const pathname = usePathname();
  const params = useSearchParams();
  const urlCard = params.get("card");
  const { stamp, setHeld } = useContext(WorkCtx);
  const open = useOpenCard();
  const [chip, setChip] = useState<Chip | null>(null);
  const day = useInboxDay();

  // Which card this tab has open, if any, on this page: one named by the address, else the one kept while the reader
  // stays under its place. The inbox itself ends it.
  useEffect(() => {
    let o = readOpen();
    if (urlCard) {
      if (o?.card !== urlCard) o = { card: urlCard, back: null, place: null };
    } else if (o && (pathname === INBOX_PATH || o.place === null || !underPlace(pathname, o.place))) {
      o = null;
    }
    writeOpen(o);
  }, [pathname, urlCard]);

  const ask = useCallback(
    async (card: string | null, signal: AbortSignal) => {
      const res = await fetch(card ? `/api/inbox/chip?card=${encodeURIComponent(card)}` : "/api/inbox/chip", {
        headers: lensKey ? { [LENS_HEADER]: lensKey } : undefined,
        cache: "no-store",
        signal,
      });
      if (!res.ok) return null;
      return (await res.json()) as { card?: Chip | null; inbox?: DayTally };
    },
    [lensKey],
  );

  useEffect(() => {
    const card = open?.card ?? null;
    // The inbox hands over its own reading (<InboxDay>): an ask here is a second moment.
    if (card === null && pathname === INBOX_PATH) return;
    const ctl = new AbortController();
    ask(card, ctl.signal)
      .then((body) => {
        if (body === null) return;
        if (body.inbox) setHeld({ lensKey, day: body.inbox });
        if (card === null) {
          setChip(null);
          return;
        }
        const c = body.card ?? null;
        if (c === null) {
          // No such card, or not the reader's to see: nothing is open.
          writeOpen(null);
          return;
        }
        setChip(c);
        const kept = readOpen();
        if (kept?.card === c.cardId && kept.place !== c.place) writeOpen({ ...kept, place: c.place });
      })
      .catch(() => {});
    return () => ctl.abort();
  }, [open?.card, pathname, stamp, ask, lensKey, setHeld]);

  const inbox = lensHref(INBOX_PATH, lensKey);
  if (open !== null && chip !== null && chip.cardId === open.card) {
    const back = open.back ?? inbox;
    return (
      <IntentLink
        href={back}
        onClick={() => writeOpen(null)}
        title="Back to the inbox, where you were"
        data-testid="task-chip"
        data-card={chip.cardId}
        data-done={chip.done ? "true" : "false"}
        className={clsx(CHIP, "flex", chip.done ? "border-dot-green/40 bg-chipc-green text-fg" : "border-accent/40 bg-accent/[.16] text-fg hover:border-accent/60")}
      >
        <span className="grid h-5 w-5 shrink-0 place-items-center rounded-md border border-line bg-surface" aria-hidden>
          <ArrowLeft size={12} />
        </span>
        {chip.done && <Check size={13} className="shrink-0 text-chipfg-green" aria-label="Done" />}
        <span className="truncate font-medium text-text-muted" data-testid="task-chip-label">
          {chip.label}
        </span>
        {chip.n && (
          <span className="tabular-nums" data-testid="task-chip-n">
            {chip.n}
          </span>
        )}
        {chip.newN && (
          <span className="tabular-nums font-medium text-text-muted" data-testid="task-chip-new">
            {chip.newN}
          </span>
        )}
      </IntentLink>
    );
  }
  return (
    <IntentLink
      href={inbox}
      title="Today's cards: done of total"
      data-testid="task-chip"
      // Off on a phone, where the top bar has no room beside the persona menu; a card being worked always shows.
      className={clsx(CHIP, "hidden sm:flex border-hairline bg-surface text-fg hover:border-line")}
    >
      <Inbox size={13} className="shrink-0" aria-hidden />
      <span>Inbox</span>
      {/* An empty day says nothing, as the summary line's "Nothing due today" does: "0/0" reads as a score. */}
      {day && day.total > 0 && (
        <span className="tabular-nums text-text-muted" data-testid="task-chip-n">
          {day.done.toLocaleString("en-US")}/{day.total.toLocaleString("en-US")}
        </span>
      )}
    </IntentLink>
  );
}
